import type { User } from "firebase/auth";
import {
  browserLocalPersistence,
  getIdTokenResult,
  onAuthStateChanged,
  setPersistence,
  signOut,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { auth, functions } from "../firebase";

export type PermissionLevel = "none" | "view" | "edit" | "manage";
export type PermissionMap = Record<string, PermissionLevel>;

type AuthState = {
  user: User | null;
  loading: boolean;

  isSuperAdmin: boolean;
  isAdmin: boolean;
  isOffice: boolean;
  isTeacher: boolean;
  isStaff: boolean;
  isDeveloper: boolean;
  isAppDeveloper: boolean;
  isWebDeveloper: boolean;
  isDeveloperAdmin: boolean;

  permissions: PermissionMap;
  hasPermission: (moduleId: string, level?: PermissionLevel) => boolean;

  refreshClaims: () => Promise<void>;
};

const Ctx = createContext<AuthState>({
  user: null,
  loading: true,
  isSuperAdmin: false,
  isAdmin: false,
  isOffice: false,
  isTeacher: false,
  isStaff: false,
  isDeveloper: false,
  isAppDeveloper: false,
  isWebDeveloper: false,
  isDeveloperAdmin: false,
  permissions: {},
  hasPermission: () => false,
  refreshClaims: async () => {},
});

const DEFAULT_IDLE_TIMEOUT_MS = 15 * 60 * 1000;

function normalizeRole(value: unknown) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

const permissionRank: Record<PermissionLevel, number> = { none: 0, view: 1, edit: 2, manage: 3 };

function normalizePermissionLevel(value: unknown): PermissionLevel {
  return value === "manage" || value === "edit" || value === "view" ? value : "none";
}

function mergePermission(target: PermissionMap, moduleId: string, level: PermissionLevel) {
  if (permissionRank[level] > permissionRank[target[moduleId] || "none"]) target[moduleId] = level;
}

function mergeHelpTopics(target: PermissionMap, ids: string[], level: PermissionLevel = "view") {
  ids.forEach((id) => mergePermission(target, `helpTopic.${id}`, level));
}

function permissionsFromClaim(value: unknown): PermissionMap {
  const out: PermissionMap = {};
  if (!value || typeof value !== "object") return out;
  Object.entries(value as Record<string, unknown>).forEach(([moduleId, level]) => {
    const normalized = normalizePermissionLevel(level);
    if (normalized !== "none") out[moduleId] = normalized;
  });
  return out;
}

function legacyPermissions(c: any, role: string): PermissionMap {
  const out: PermissionMap = {};
  const superAdmin = c.superAdmin === true || role === "superadmin";
  const admin = c.admin === true || role === "admin" || superAdmin;
  const office = c.office === true || role === "office" || admin || superAdmin;
  const teacher = c.teacher === true || role === "teacher" || admin || superAdmin;
  const appDeveloper = c.appDeveloper === true || c.developer === true || role === "developer" || role === "appdeveloper" || superAdmin;
  const webDeveloper = c.webDeveloper === true || role === "webdeveloper" || superAdmin;

  if (teacher || office || admin || superAdmin) {
    ["dashboard", "calendar", "news", "messaging", "familyCrm"].forEach((id) => mergePermission(out, id, "edit"));
    mergeHelpTopics(out, ["getting-started", "dashboard", "calendar", "news", "messaging", "family-crm"]);
  }
  if (office || admin || superAdmin) {
    [
      "admissionsCrm",
      "donorCrm",
      "roster",
      "families",
      "schoolYear",
      "volunteerReview",
      "volunteerOps",
      "publicWebPages",
      "publicWebTheme",
      "publicWebForms",
      "publicWebPeople",
      "publicWebActivity",
    ].forEach((id) => mergePermission(out, id, "manage"));
    mergeHelpTopics(out, [
      "admissions-crm",
      "donor-crm",
      "bulk-email",
      "roster",
      "roster-manual-entry-best-practice",
      "roster-students-manual-entry",
      "roster-parents-manual-entry",
      "roster-links-manual-entry",
      "roster-import-csv-xlsx",
      "families",
      "school-year",
      "volunteer",
      "public-pages-cms",
      "public-theme",
      "public-forms",
      "public-people",
      "newsletters",
    ]);
  }
  if (admin || superAdmin) {
    [
      "messageHistory",
      "volunteerSettings",
      "clubs",
      "ptuMessaging",
      "ptuSignups",
      "ptuSettings",
      "publicWebAnalytics",
      "users",
      "settings",
      "auditLogs",
      "accessControl",
    ].forEach((id) => mergePermission(out, id, "manage"));
    mergePermission(out, "helpCenter", "manage");
    mergeHelpTopics(out, ["clubs", "web-analytics", "users", "roles-permissions", "settings", "audit-logs"]);
  }
  if (appDeveloper || webDeveloper || superAdmin) mergePermission(out, "developerPortal", "view");
  if (webDeveloper || superAdmin) {
    ["publicWebPages", "publicWebTheme", "publicWebPeople", "publicWebActivity"].forEach((id) => mergePermission(out, id, "manage"));
    mergeHelpTopics(out, ["getting-started", "public-pages-cms", "public-theme", "public-people", "newsletters", "developer-portal"]);
  }
  if (superAdmin) {
    ["advancedTools", "dataPurge", "superAdminAccess", "developerPortal", "techInventory"].forEach((id) => mergePermission(out, id, "manage"));
    mergeHelpTopics(out, ["developer-portal", "advanced-tools"]);
  }
  return out;
}

function mergePermissions(...maps: PermissionMap[]): PermissionMap {
  const out: PermissionMap = {};
  maps.forEach((map) => {
    Object.entries(map).forEach(([moduleId, level]) => mergePermission(out, moduleId, level));
  });
  return out;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOffice, setIsOffice] = useState(false);
  const [isTeacher, setIsTeacher] = useState(false);
  const [isDeveloper, setIsDeveloper] = useState(false);
  const [isAppDeveloper, setIsAppDeveloper] = useState(false);
  const [isWebDeveloper, setIsWebDeveloper] = useState(false);
  const [isDeveloperAdmin, setIsDeveloperAdmin] = useState(false);
  const [permissions, setPermissions] = useState<PermissionMap>({});

  const idleTimerRef = useRef<number | null>(null);
  const [idleTimeoutMs, setIdleTimeoutMs] = useState<number>(DEFAULT_IDLE_TIMEOUT_MS);

  async function readClaims(u: User | null) {
    if (!u) {
      setIsSuperAdmin(false);
      setIsAdmin(false);
      setIsOffice(false);
      setIsTeacher(false);
      setIsDeveloper(false);
      setIsAppDeveloper(false);
      setIsWebDeveloper(false);
      setIsDeveloperAdmin(false);
      setPermissions({});
      return;
    }

    try {
      await u.reload();
    } catch {
      // ignore
    }

    const tok = await getIdTokenResult(u, true);
    const c: any = tok?.claims ?? {};
    const role = normalizeRole(c.role);

    const nextPermissions = mergePermissions(legacyPermissions(c, role), permissionsFromClaim(c.p));
    const has = (moduleId: string, level: PermissionLevel = "view") =>
      permissionRank[nextPermissions[moduleId] || "none"] >= permissionRank[level];

    const superAdmin = c.superAdmin === true || role === "superadmin" || has("advancedTools", "manage") || has("superAdminAccess", "manage");
    const admin = c.admin === true || role === "admin" || superAdmin || has("settings", "view") || has("users", "view") || has("accessControl", "view");
    const office = c.office === true || role === "office" || admin || superAdmin || has("roster", "view") || has("families", "view") || has("admissionsCrm", "view");
    const teacher = c.teacher === true || role === "teacher" || admin || superAdmin || has("news", "view") || has("messaging", "view") || has("familyCrm", "view");
    const appDeveloper = c.appDeveloper === true || c.developer === true || role === "developer" || role === "appdeveloper" || superAdmin || has("developerPortal", "manage");
    const webDeveloper = c.webDeveloper === true || role === "webdeveloper" || superAdmin || (has("developerPortal", "view") && has("publicWebPages", "manage"));
    const developer = appDeveloper || webDeveloper;
    const developerAdmin = c.developerAdmin === true || superAdmin || has("superAdminAccess", "manage");

    setIsSuperAdmin(superAdmin);
    setIsAdmin(admin);
    setIsOffice(office);
    setIsTeacher(teacher);
    setIsDeveloper(developer);
    setIsAppDeveloper(appDeveloper);
    setIsWebDeveloper(webDeveloper);
    setIsDeveloperAdmin(developerAdmin);
    setPermissions(nextPermissions);
  }

  useEffect(() => {
    let unsub = () => {};

    (async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
      } catch (e) {
        console.warn("[AuthProvider] Failed to set browser auth persistence:", e);
      }

      unsub = onAuthStateChanged(auth, async (u) => {
        setUser(u);
        try {
          await readClaims(u);
        } finally {
          setLoading(false);
        }
      });
    })();

    return () => unsub();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadSessionPolicy() {
      if (!user) {
        setIdleTimeoutMs(DEFAULT_IDLE_TIMEOUT_MS);
        return;
      }

      try {
        const fn = httpsCallable(functions, "getSessionPolicy");
        const res: any = await fn({});
        const minutes = Number(res?.data?.effectiveIdleTimeoutMinutes);
        const ms = Number.isFinite(minutes) && minutes > 0 ? minutes * 60 * 1000 : DEFAULT_IDLE_TIMEOUT_MS;
        if (!cancelled) {
          setIdleTimeoutMs(ms);
        }
      } catch (e) {
        console.warn("[AuthProvider] Failed to load session policy:", e);
        if (!cancelled) {
          setIdleTimeoutMs(DEFAULT_IDLE_TIMEOUT_MS);
        }
      }
    }

    loadSessionPolicy();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, isTeacher, isOffice, isAdmin, isSuperAdmin]);

  useEffect(() => {
    if (!user) {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }

    const resetIdleTimer = () => {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
      }

      idleTimerRef.current = window.setTimeout(async () => {
        try {
          await signOut(auth);
        } finally {
          window.location.href = "/login?reason=idle";
        }
      }, idleTimeoutMs);
    };

    const events: Array<keyof WindowEventMap> = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((evt) => window.addEventListener(evt, resetIdleTimer, { passive: true }));
    resetIdleTimer();

    return () => {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      events.forEach((evt) => window.removeEventListener(evt, resetIdleTimer));
    };
  }, [user, idleTimeoutMs]);

  const value = useMemo<AuthState>(() => {
    const hasPermission = (moduleId: string, level: PermissionLevel = "view") => {
      return permissionRank[permissions[moduleId] || "none"] >= permissionRank[level];
    };

    return {
      user,
      loading,
      isSuperAdmin,
      isAdmin,
      isOffice,
      isTeacher,
      isStaff: isTeacher || isOffice || isAdmin || isSuperAdmin,
      isDeveloper,
      isAppDeveloper,
      isWebDeveloper,
      isDeveloperAdmin,
      permissions,
      hasPermission,
      refreshClaims: async () => {
        if (!auth.currentUser) return;
        await readClaims(auth.currentUser);
      },
    };
  }, [user, loading, isSuperAdmin, isAdmin, isOffice, isTeacher, isDeveloper, isAppDeveloper, isWebDeveloper, isDeveloperAdmin, permissions]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  return useContext(Ctx);
}
